import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { readAgentYaml } from "../../lib/agent-yaml.js";
import { unlinkBrain } from "../../lib/brain-unlink.js";
import { discoverKvUri } from "../../lib/brain-paths.js";

export class BrainUnlinkCommand extends M8tCommand {
  static paths = [["brain", "unlink"]];
  static usage = Command.Usage({
    description: "Unlink a worker from its brain repo (cascade teardown).",
    details:
      "Re-deploys the agent without the brain loader + MCP tool, deletes the Foundry connection, and (unless --keep-app-install) uninstalls the GitHub App on the repo. The repo itself is NOT deleted.",
    examples: [
      ["Unlink cmo non-interactively", "$0 brain unlink cmo --yes"],
      ["Unlink but keep the App install", "$0 brain unlink cmo --yes --keep-app-install"],
    ],
  });

  worker = Option.String();
  yes = Option.Boolean("--yes", false);
  keepAppInstall = Option.Boolean("--keep-app-install", false);
  kvUri = Option.String("--kv-uri");
  subscription = Option.String("--subscription");
  endpoint = Option.String("--endpoint");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    // Normalize clipanion descriptor objects that appear when flags are unset in tests.
    const worker = typeof this.worker === "string" ? this.worker : undefined;
    if (!worker) {
      throw new LocalCliError({ code: "USAGE", message: "<worker> positional argument is required" });
    }

    const env = this.context.env;
    const kvUri = discoverKvUri(env, typeof this.kvUri === "string" ? this.kvUri : undefined);

    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);
    const progress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    // Normalize booleans: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests, it's the actual boolean value.
    // Only treat as true when it's literally `true` — anything else (descriptor, false) = false.
    const yes = this.yes === true;
    const keepAppInstall = this.keepAppInstall === true;

    // Read per-agent yaml to surface the brain block early for the confirm message.
    const yaml = readAgentYaml(worker);
    if (!yaml?.brain) {
      throw new LocalCliError({
        code: "BRAIN_NOT_LINKED",
        message: `Agent '${worker}' is not linked to a brain.`,
        hint: "Run 'm8t brain link' or 'm8t brain create' to establish a link first.",
      });
    }
    const { repo } = yaml.brain;

    // Confirm gate: require --yes in non-TTY mode (interactive TTY prompt is deferred).
    const isTTY = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    if (!yes) {
      if (!isTTY) {
        throw new LocalCliError({
          code: "UNLINK_NO_CONFIRM",
          message: `Refusing to unlink '${worker}' from '${repo}' without explicit confirmation. Pass --yes to confirm non-interactively.`,
          hint: "Pass --yes to unlink non-interactively, or re-run in a TTY.",
        });
      }
      // Interactive TTY prompt — deferred to a follow-up.
      throw new LocalCliError({
        code: "INTERACTIVE_PROMPT_NOT_IMPLEMENTED",
        message: `Interactive confirm prompt not yet implemented. Pass --yes to confirm.`,
        hint: "Pass --yes to unlink non-interactively.",
      });
    }

    const credential = new DefaultAzureCredential();

    // Resolve Foundry project for ARM id.
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;
    const agentEndpoint = yaml.projectEndpoint;
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: (this.context.stdout as NodeJS.WriteStream).isTTY === true,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      agentEndpoint,
    });

    const result = await unlinkBrain({
      credential,
      kvUri,
      projectEndpoint: project.endpoint,
      projectArmId: `${project.accountScope}/projects/${project.projectName}`,
      agentName: worker,
      keepAppInstall,
      onProgress: progress,
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          worker,
          repo,
          foundryVersion: result.foundryVersion,
          connectionDeleted: result.connectionDeleted,
          appUninstalled: result.appUninstalled,
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} unlinked ${colors.field(worker)} from ${colors.field(repo)}. Repo intact — run ${colors.field(`gh repo delete ${repo}`)} to remove it.\n`,
    );
    if (!keepAppInstall && result.appUninstalled) {
      this.context.stdout.write(
        `  ${colors.hint("app install removed:")} re-link anytime with \`m8t brain link ${worker} --repo ${repo}\` — no re-registration needed.\n`,
      );
    } else if (keepAppInstall) {
      this.context.stdout.write(
        `  ${colors.hint("note:")} App install kept (--keep-app-install). Run \`m8t brain link ${worker} --repo ${repo}\` to re-link.\n`,
      );
    }
    return 0;
  }
}
